import { Attachment } from "@mui/icons-material";
import { Alert, Grid, Stack, Typography } from "@mui/material";
import { FileGroup } from "common/FileUpload";
import { useEffect, useMemo, useState } from "react";
import { getFiles } from "src/apis/controllers/files";
import { FileProvider, useFileContext } from "src/context/FilesV2";
import CustomSimpleAccordion from "src/qubCommon/Accordions/CustomSimpleAccordion";
import { downloadFile, formatFiles } from "src/util";

const PAYMENT_ATTACHMENTS_PROJECT_TABLE_ID = 18;

const noopFileAction = async () => {};
const noopUploadFiles = async () => [];

const getAttachmentRequest = (paymentTransHeaderId) => ({
  DataKey: "MHD_TransHeader_Expenses",
  DataValue: null,
  RecordId: null,
  RecordGuid: paymentTransHeaderId ?? null,
  ProjectTableId: PAYMENT_ATTACHMENTS_PROJECT_TABLE_ID,
});

const getTitlePart = (value) => {
  const titlePart = value?.toString?.().trim?.() ?? "";
  return titlePart || null;
};

const getPaymentTitle = (payment) => {
  const title = [
    getTitlePart(payment?.transTypeName),
    getTitlePart(payment?.paymentNumber),
    getTitlePart(payment?.paymentDateFormatted),
  ]
    .filter(Boolean)
    .join(" - ");

  return title || "Πληρωμή";
};

const hasBundleFiles = (group) =>
  group?.bundles?.some((bundle) => bundle?.files?.length > 0) === true;

const getVisibleFileGroup = (group) => ({
  ...group,
  bundles: group?.bundles?.filter((bundle) => bundle?.files?.length > 0) ?? [],
});

const PaymentAccordionTitle = ({ payment }) => (
  <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
    <Attachment color="primary" fontSize="small" sx={{ ml: "2px" }} />
    <Typography color="primary" sx={{ fontWeight: 1000 }}>
      ΣΥΝΗΜΜΕΝΑ ΠΛΗΡΩΜΗΣ
    </Typography>
    <Typography
      sx={{
        color: "#344054",
        fontSize: 13,
        fontWeight: 800,
      }}
    >
      {getPaymentTitle(payment)}
    </Typography>
  </Stack>
);

const PaymentAttachmentAccordionContent = ({ payment, refreshKey = 0 }) => {
  const { control: filesControl, fileGroups } = useFileContext();
  const paymentTransHeaderId = payment?.paymentTransHeaderId;
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const visibleFileGroups = useMemo(
    () => (fileGroups ?? []).map(getVisibleFileGroup).filter(hasBundleFiles),
    [fileGroups],
  );

  useEffect(() => {
    if (!paymentTransHeaderId) {
      filesControl.initialize([]);
      filesControl.setIsDirty?.(false);
      return undefined;
    }

    let isActive = true;

    const fetchPaymentAttachments = async () => {
      setIsLoading(true);
      setErrorMessage(null);

      try {
        const res = await getFiles(getAttachmentRequest(paymentTransHeaderId));
        if (!isActive) return;

        filesControl.initialize(formatFiles(res?.data ?? []));
        filesControl.setIsDirty?.(false);
      } catch (error) {
        if (!isActive) return;

        filesControl.initialize([]);
        filesControl.setIsDirty?.(false);
        setErrorMessage(
          error?.message ??
            "Δεν φορτώθηκαν τα συνημμένα της πληρωμής.",
        );
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    fetchPaymentAttachments();

    return () => {
      isActive = false;
    };
  }, [paymentTransHeaderId, refreshKey]);

  if (isLoading) {
    return (
      <Alert severity="info">Φόρτωση συνημμένων πληρωμής...</Alert>
    );
  }

  if (errorMessage) {
    return <Alert severity="warning">{errorMessage}</Alert>;
  }

  if (visibleFileGroups.length === 0) {
    return (
      <Typography sx={{ color: "#667085", fontSize: 13 }}>
        Δεν βρέθηκαν συνημμένα πληρωμής.
      </Typography>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {visibleFileGroups.map((group) => (
          <FileGroup
            reviewMode={false}
            isReadOnly
            canAdd={false}
            canDelete={false}
            key={group.id}
            id={group.id}
            title={group?.title}
            maxSize={2097152}
            maxFiles={5}
            maxBundles={group?.maxBundles}
            fields={group.fields}
            bundles={group?.bundles}
            templates={group?.templates}
          />
        ))}
      </Grid>
    </Grid>
  );
};

const PaymentAttachmentAccordion = ({ payment, refreshKey = 0 }) => (
  <FileProvider
    downloadFile={(fileId, type) => downloadFile(fileId, type)}
    uploadFiles={noopUploadFiles}
    onDeleteBundle={noopFileAction}
    onDeleteFile={noopFileAction}
  >
    <CustomSimpleAccordion
      title={<PaymentAccordionTitle payment={payment} />}
      content={
        <PaymentAttachmentAccordionContent
          payment={payment}
          refreshKey={refreshKey}
        />
      }
    />
  </FileProvider>
);

const PaymentAttachmentsReadOnly = ({ payments = [], refreshKey = 0 }) => {
  const validPayments = (payments ?? []).filter(
    (payment) => payment?.paymentTransHeaderId,
  );

  if (validPayments.length === 0) return null;

  return (
    <Stack spacing={1}>
      {validPayments.map((payment) => (
        <PaymentAttachmentAccordion
          key={payment.paymentTransHeaderId}
          payment={payment}
          refreshKey={refreshKey}
        />
      ))}
    </Stack>
  );
};

export default PaymentAttachmentsReadOnly;
